import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';

import { DomainPermission } from '../../types/DomainPermission';
import { DomainValidationStatus } from '../../types/DomainValidationStatus';
import { PoolAssignmentOption } from '../../types/PoolAssignmentOption';
import { PoolID } from '../../types/PoolID';
import { validateDomainName } from '../../utils/DomainUtilities';
import { AddNewDomainDialogBody } from './AddNewDomainDialogBody';

export function openAddNewDomainDialog(
  onAddDomainDialogSave: (domainName: string, poolID: PoolID | null, domainPermission: DomainPermission) => void,
  poolOptions: ReadonlyArray<PoolAssignmentOption>,
  otherDomainNames: ReadonlyArray<string>
) {
  const action = (_props: AddNewDomainDialogInputProps, state: AddNewDomainDialogState) => {
    onAddDomainDialogSave(state.domainName, state.poolID, state.domainPermission);
    return Promise.resolve();
  };
  const dialogInstance = openReactActionDialog({
    title: Messages.bridgeSiteSettingsAddNewDomain(),
    bodyComponent: AddNewDomainDialog,
    initialBodyProps: { poolOptions, otherDomainNames },
    initialDialogState: { domainName: '', poolID: null, domainPermission: DomainPermission.ALLOW, domainValidationStatus: DomainValidationStatus.NO_ERROR },
    actionDisabled: (_props, state) => {
      return state.domainValidationStatus !== DomainValidationStatus.NO_ERROR || (state.poolID === null && state.domainPermission === DomainPermission.ALLOW);
    },
    actionName: Messages.save(),
    action: action,
    isActionDelete: false,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false
  });

  return Promise.resolve(dialogInstance.result);
}

export type AddNewDomainDialogInputProps = Readonly<{
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  otherDomainNames: ReadonlyArray<string>;
}>;

export type AddNewDomainDialogState = Readonly<{
  domainName: string;
  poolID: PoolID | null;
  domainPermission: DomainPermission;
  domainValidationStatus: DomainValidationStatus;
}>;

export type AddNewDomainDialogProps = ReactActionDialogBodyProps<
  AddNewDomainDialogInputProps,
  AddNewDomainDialogState
>;

export const AddNewDomainDialog = (props: AddNewDomainDialogProps): JSX.Element => {
  const setDomainValidationStatus = (validationStatus: DomainValidationStatus) => props.updateDialogState({ domainValidationStatus: validationStatus });
  const setDomainName = (changedDomainName: string) => props.updateDialogState({ domainName: changedDomainName });
  const setAssignedPoolID = (changedPoolID: PoolID | null) => props.updateDialogState({ poolID: changedPoolID });
  const setDomainPermission = (changedDomainPermission: DomainPermission) => props.updateDialogState({ domainPermission: changedDomainPermission });

  const onDomainNameChange = (changedDomainName: string) => {
    setDomainValidationStatus(validateDomainName(changedDomainName, props.otherDomainNames));
    setDomainName(changedDomainName);
  };
  const onDomainPermissionChange = (changedDomainPermission: DomainPermission) => {
    setDomainPermission(changedDomainPermission);
  };
  const onDomainPoolAssignmentChange = (changedPoolID: PoolID | null) => {
    setAssignedPoolID(changedPoolID);
  };

  return (
    <AddNewDomainDialogBody
      onDomainNameChange={onDomainNameChange}
      domainName={props.dialogState.domainName}
      onDomainPermissionChange={onDomainPermissionChange}
      domainPermission={props.dialogState.domainPermission}
      onPoolAssignmentChange={onDomainPoolAssignmentChange}
      poolOptions={props.poolOptions}
      assignedPoolID={props.dialogState.poolID}
      domainValidationStatus={props.dialogState.domainValidationStatus}
    />
  );
};
